import { Criterion, CriteriaGroup, IMPORTANCE_WEIGHTS, DEFAULT_VALUE_SCALE } from '@/types/evaluation.types';

/**
 * Calculate criterion weights for all criteria in a group based on importance.
 * Ensures weights sum to 1.0 within the group.
 * Criteria with Importance.None get weight of 0.
 *
 * @param criteria Array of criteria in a group
 * @returns Array of weights (0-1) corresponding to each criterion
 */
export function calculateCriterionWeights(criteria: Criterion[]): number[] {
  const importanceNumbers = criteria.map(c => IMPORTANCE_WEIGHTS[c.importance]);
  const sum = importanceNumbers.reduce((acc, val) => acc + val, 0 as number);

  if (sum === 0) {
    // All criteria are Importance.None - return zeros
    return criteria.map(() => 0);
  }

  return importanceNumbers.map(num => num / sum);
}

/**
 * Update criterion weights for a criteria group.
 * Call this whenever criteria are added, removed, or importance changes.
 *
 * @param group Criteria group to update
 * @returns Updated criteria group with recalculated criterion weights
 */
export function updateCriterionWeights(group: CriteriaGroup): CriteriaGroup {
  const weights = calculateCriterionWeights(group.criteria);

  return {
    ...group,
    criteria: group.criteria.map((criterion, index) => ({
      ...criterion,
      criterionWeight: weights[index],
      valueScale: criterion.valueScale ?? DEFAULT_VALUE_SCALE,
    })),
  };
}

/**
 * Validate that all group weights sum to 1.0 (with tolerance for floating point errors)
 *
 * @param groups Array of criteria groups
 * @returns true if weights sum to 1.0 (within tolerance)
 */
export function validateGroupWeights(groups: CriteriaGroup[]): boolean {
  const sum = groups.reduce((acc, g) => acc + (g.groupWeight ?? g.weight / 100), 0);
  return Math.abs(sum - 1.0) < 0.001; // Tolerance for floating point errors
}

/**
 * Convert legacy percentage weights to decimal groupWeights.
 * Provides backward compatibility for existing data.
 *
 * @param group Criteria group potentially with old format
 * @returns Group with groupWeight field populated
 */
export function migrateGroupWeights(group: CriteriaGroup): CriteriaGroup {
  return {
    ...group,
    groupWeight: group.groupWeight ?? group.weight / 100,
  };
}

/**
 * Normalize group weights to sum to 1.0.
 * Useful when weights don't sum to exactly 1.0 due to rounding or user input.
 *
 * @param groups Array of criteria groups
 * @returns Groups with normalized weights
 */
export function normalizeGroupWeights(groups: CriteriaGroup[]): CriteriaGroup[] {
  const total = groups.reduce((sum, g) => sum + (g.groupWeight ?? g.weight / 100), 0);

  if (total === 0) {
    // If all weights are 0, distribute evenly
    const evenWeight = 1.0 / groups.length;
    return groups.map(g => ({
      ...g,
      groupWeight: evenWeight,
      weight: evenWeight * 100,
    }));
  }

  return groups.map(g => {
    const currentWeight = g.groupWeight ?? g.weight / 100;
    const normalizedWeight = currentWeight / total;
    return {
      ...g,
      groupWeight: normalizedWeight,
      weight: normalizedWeight * 100,
    };
  });
}
